import React, { useState } from "react";
import {
  MantineProvider,
  LoadingOverlay,
  Stack,
  Title,
  Text,
  Button,
  getDefaultZIndex,
} from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { DatesProvider } from "@mantine/dates";
import * as Sentry from "@sentry/react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  Navigate,
  useParams,
  Outlet,
} from "react-router-dom";

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const AppErrorFallback = () => (
  <Stack align="center" justify="center" h="100dvh">
    <Title order={2}>Something went wrong</Title>
    <Text c="dimmed">Our team has been notified. Please refresh the page.</Text>
    <Button onClick={() => window.location.reload()}>Refresh</Button>
  </Stack>
);
import MantineLoginPage from "./components/MantineLoginPage";
import MantineSignUpPage from "./components/MantineSignUpPage";
import VerifyEmail from "./components/VerifyEmail";
import BusinessInfoPage from "./components/BusinessInfoPage";
import AddEmployeesPage from "./components/AddEmployeesPage";
import AddServicesPage from "./components/AddServicesPage";
import BusinessHoursPage from "./components/BusinessHoursPage";
import CalendarView from "./components/calendar/CalendarView/CalendarViewWithOnboardingCheck";
import { CashDrawerPage } from "./components/cashDrawer/CashDrawerPage";
import ClientsRoutes from "./components/clients/ClientsRoutes";
import ProductsPage from "./components/products/ProductsPage";
import "@mantine/core/styles.css";
import "./App.css";
import "./theme/fonts.css";
import { brandTheme, brandCssVariablesResolver } from "./theme/brandTheme";
import { AppProvider, useApp } from "./context/AppContext";
import {
  NavigationLoadingProvider,
  NavigationLoadingOverlay,
} from "./context/NavigationLoadingContext";
import { NavigationGuardProvider } from "./context/NavigationGuardContext";
import { TransactionProvider } from "./context/TransactionContext";
import { ProductsProvider } from "./context/ProductsContext";
import { TemplateProvider } from "./context/TemplateContext";
import ForgotPassword from "./components/ForgotPassword";
import SettingsPage from "./components/settings/SettingsPage";
import ChangeTemporaryPassword from "./components/ChangeTemporaryPassword";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SalonProvider, useSalon } from "./context/SalonContext";
import { LoyaltyProgramProvider } from "./context/LoyaltyProgramContext";
import { DiscountPresetsProvider } from "./context/DiscountPresetsContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import { ServiceAdjustmentFormsProvider } from "./context/ServiceAdjustmentFormsContext";
import { FormTemplatesProvider } from "./context/FormTemplatesContext";
import { ClientFormsProvider } from "./context/ClientFormsContext";
import { ServiceRequiredFormsProvider } from "./context/ServiceRequiredFormsContext";
import { ServicePreAppointmentDocumentsProvider } from "./context/ServicePreAppointmentDocumentsContext";
import { ServicePostAppointmentProvider } from "./context/ServicePostAppointmentContext";
import { CalendarProvider } from "./context/CalendarContext";
import { CalendarEventProvider } from "./context/CalendarEventContext";
import { ClientProvider } from "./context/ClientContext";
import { useClientSyncListener } from "./hooks/useClientSyncListener";
import { CampaignProvider } from "./context/CampaignContext";
import { MessagingProvider } from "./context/MessagingContext";
import ChatPage from "./components/chat/ChatPage";
import { NotificationProvider } from "./context/NotificationContext";
import PaymentsPage from "./components/payments/PaymentsPage";
import SalesPage from "./components/sales/SalesPage";
import { isSalesTabEnabled } from "./utils/salesTabFlag";
import { isMessagingEnabled } from "./utils/messagingFlag";
import { StripePaymentsProvider } from "./context/StripePaymentsContext";
import { ResponsiveProvider } from "./context/ResponsiveContext";
import {
  FeatureAccessProvider,
  useFeatureAccess,
} from "./context/FeatureAccessContext";
import FeatureGate from "./components/common/FeatureGate";
import PermissionGate from "./components/common/PermissionGate";
import usePermissions from "./hooks/usePermissions";
import AppLayout from "./components/Layout/AppLayout";
import MarketingPageShell from "./components/Marketing/MarketingPageShell";
import ClientGroupDetailPage from "./components/clients/groups/ClientGroupDetailPage";
import CampaignWizard from "./components/Marketing/CampaignWizard";
import UnauthorizedPage from "./components/common/UnauthorizedPage";
import LandingRedirect from "./components/common/LandingRedirect";
import LocationPickerScreen from "./components/location/LocationPickerScreen";
import ViewCampaign from "./components/Marketing/ViewCampaign";
import PusherInitializer from "./components/common/PusherInitializer";
import ReleaseNoteWatcher from "./components/notifications/ReleaseNoteWatcher";
import PageMoved from "./components/common/PageMoved";
import WebsiteGeneratorWizard from "./components/StaticWebGenerator/components/WebsiteGeneratorWizard";
import { WEBSITE_FEATURES } from "./components/StaticWebGenerator/utils/FeatureLocks";
import ReportsRoutes from "./components/reports/ReportsRoutes";
import SsoInitiate from "./components/common/SsoInitiate";
import PayrollPage from "./components/payroll/PayrollPage";
import VoiceAIPage from "./components/VoiceAI/VoiceAIPage";
import OnboardingPage from "./components/onboarding/OnboardingPage";
import RewardsPage from "./components/rewards/RewardsPage";
import LoyaltyReportingPage from "./components/rewards/LoyaltyReportingPage";
import AccountCreationPage from "./components/onboarding/AccountCreationPage";
import DataMigrationDocPage from "./components/onboarding/DataMigrationDocPage";

// Protected Route component - uses access.user_level for fast role checks
// Redirects to /admin/calendar if user doesn't have the required role
const ProtectedRoute = ({ allowedRoles, children }) => {
  const { currentUser } = useSalon();
  const { access, isLoading: isAccessLoading } = useFeatureAccess();

  // Use access.user_level for immediate role check (faster than waiting for staff fetch)
  const userRole = access?.user_level || currentUser?.role;

  // Still loading access data - show nothing briefly
  if (isAccessLoading && !userRole) {
    return null;
  }

  // If we have a role and it doesn't match, redirect to calendar
  if (userRole && !allowedRoles.includes(userRole)) {
    return <Navigate to="/admin/calendar" replace />;
  }

  // Allow access - either role matches or we're still determining role
  return children;
};

// Marketing Routes Layout - section access from section_permissions (marketing.canView)
const MarketingRoutes = () => {
  const perms = usePermissions();
  const canViewMarketing =
    perms?.marketing == null || perms.marketing.canView !== false;
  if (!canViewMarketing) {
    return <Navigate to="/admin/calendar" replace />;
  }
  return (
    <CampaignProvider>
      <Outlet />
    </CampaignProvider>
  );
};

// Chat Routes Layout — default-deny: the two-way inbox shows only when the
// `messaging` RBAC permission allows it AND the location is on the Twilio
// platform (end_user_messaging locations have no inbox). The REACT_APP_MESSAGING_ENABLED
// flag gates whether the routes mount at all (see the admin routes below).
const MessagingRoutes = () => {
  const perms = usePermissions();
  const { messagingPlatform } = useFeatureAccess();
  const canViewMessaging = perms?.messaging?.canView === true;
  if (!canViewMessaging || messagingPlatform !== "twilio") {
    return <Navigate to="/admin/calendar" replace />;
  }
  // MessagingProvider is mounted app-wide (see App()), so the routes just gate.
  return <Outlet />;
};

function AppContent() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, setIsLoading } = useAuth();
  const { updateSalon, updateStaff } = useApp();
  const {
    services,
    setBusinessInfo,
    setEmployees,
    setOnboardingStage,
    setIsMissingSalon,
    // Multi-location (PR A / #399)
    availableLocations,
    selectedSalonId,
    isLocationPickerOpen,
  } = useSalon();
  const {
    access,
    isLoading: isAccessLoading,
    refreshAccess,
  } = useFeatureAccess();
  const [setUserData] = useState();

  // Cross-client refetch on client CRUD from another tab / device.
  // Consumes NotificationContext.pusherSyncEvents and calls into
  // ClientContext, both of which are provider-parents of AppContent.
  useClientSyncListener();

  const handlePageChange = (page) => {
    navigate(`/${page}`);
  };

  const handleSignUpSubmit = (signupData) => {
    setUserData(signupData);
    navigate("/business-info", { state: { from: "signup" } });
  };

  const handleEmployeesSubmit = (updatedEmployees) => {
    setIsMissingSalon(false);
    if (updatedEmployees) {
      setEmployees(updatedEmployees);
    }
    navigate("/add-services", { state: { from: "add-employees" } });
  };

  const handleBusinessHoursSubmit = async (
    hoursData,
    isOnboarding = false,
    options = {},
  ) => {
    const { skipSalonPersist = false } = options;
    try {
      setIsLoading(true);

      if (!hoursData) {
        if (isOnboarding) {
          navigate("/add-employees", { state: { from: "business-hours" } });
        }
      } else {
        const salonUpdateResponse = skipSalonPersist
          ? hoursData
          : await updateSalon(hoursData);

        setBusinessInfo((prevBusinessInfo) => ({
          ...prevBusinessInfo,
          ...salonUpdateResponse,
        }));

        try {
          await refreshAccess();
        } catch (accessErr) {
          console.error(
            "Error refreshing access after salon update:",
            accessErr,
          );
        }

        if (isOnboarding) {
          const employeeHoursData = {
            ...Object.entries(hoursData).reduce((acc, [key, value]) => {
              if (key.endsWith("_open_time")) {
                const newKey = key.replace("_open_time", "_start_time");
                return { ...acc, [newKey]: value };
              }
              return { ...acc, [key]: value };
            }, {}),
          };
          delete employeeHoursData.id;

          const employeeUpdateResponse = updateStaff({
            ...employeeHoursData,
          });
          if (employeeUpdateResponse) {
            setEmployees((prevEmployees) => [
              {
                ...prevEmployees[0],
                ...employeeHoursData,
              },
              ...(prevEmployees.slice(1) || []),
            ]);
            navigate("/add-employees", { state: { from: "business-hours" } });
          }
        }
      }
    } catch (error) {
      console.error("Error updating salon:", error);
    } finally {
      setIsLoading(false);
      setIsMissingSalon(false);
    }
  };

  const handleServicesSubmit = (isOnboarding = false) => {
    setIsMissingSalon(false);
    setOnboardingStage(null);

    if (isOnboarding) {
      navigate("/admin/calendar", { state: { from: "add-services" } });
    }
  };

  // Show loading spinner while fetching access data (user_level + onboarding).
  // Gate on `!access` so a background refreshAccess() — e.g. after editing a
  // staff member — doesn't unmount the entire route tree (which would blank
  // the screen, tear down Pusher subscriptions, and force a remount).
  if (isLoading || (isAccessLoading && !access)) {
    return <LoadingOverlay visible />;
  }

  if (!isAuthenticated) {
    return (
      <SentryRoutes>
        <Route
          path="/login"
          element={<MantineLoginPage onPageChange={handlePageChange} />}
        />
        <Route
          path="/signup"
          element={
            <MantineSignUpPage
              onSubmit={handleSignUpSubmit}
              onPageChange={handlePageChange}
            />
          }
        />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/change-password" element={<ChangeTemporaryPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </SentryRoutes>
    );
  }

  // Multi-location (PR A / #399): full-page location picker. Two contexts, one
  // component. First sign-in — a multi-location user with no saved selection
  // must pick before landing anywhere. Manual switch — the NavBar building icon
  // opens it as an overlay. Inert when the backend flag is OFF (no
  // available_locations ⇒ length 0 ⇒ neither branch fires).
  const showFirstSignInPicker =
    (availableLocations?.length || 0) > 1 && selectedSalonId == null;
  if (showFirstSignInPicker || isLocationPickerOpen) {
    return <LocationPickerScreen />;
  }

  return (
    <SentryRoutes>
      {/* Account Creation Route (Initial Onboarding - 5 steps) - Only for Owners */}
      <Route
        path="/account-creation"
        element={
          <ProtectedRoute allowedRoles={["Owner"]}>
            <AccountCreationPage />
          </ProtectedRoute>
        }
      />
      {/* Data migration documentation - Only for Owners */}
      <Route
        path="/onboarding/data-migration"
        element={
          <ProtectedRoute allowedRoles={["Owner"]}>
            <AppLayout>
              <DataMigrationDocPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Progressive Onboarding Route (Employees, Services, Payments) - Only for Owners */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute allowedRoles={["Owner"]}>
            <AppLayout>
              <OnboardingPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Legacy Onboarding Routes */}
      <Route path="/business-info" element={<BusinessInfoPage />} />
      <Route
        path="/business-hours"
        element={<BusinessHoursPage onSubmit={handleBusinessHoursSubmit} />}
      />
      <Route
        path="/add-employees"
        element={<AddEmployeesPage onSubmit={handleEmployeesSubmit} />}
      />
      <Route
        path="/add-services"
        element={
          <AddServicesPage
            onSubmit={handleServicesSubmit}
            initialServices={services}
          />
        }
      />

      {/* SSO Route */}
      <Route path="/sso/initiate-payroll" element={<SsoInitiate />} />

      {/* Admin Routes */}
      <Route
        path="/admin/*"
        element={
          <AppLayout>
            <PusherInitializer />
            <ReleaseNoteWatcher />
            <Routes>
              <Route path="calendar" element={<CalendarView />} />
              <Route
                path="cash-drawer"
                element={
                  <PermissionGate section="cashRegister">
                    <CashDrawerPage />
                  </PermissionGate>
                }
              />
              <Route
                path="products"
                element={
                  <PermissionGate section="products">
                    <ProductsPage />
                  </PermissionGate>
                }
              />
              <Route
                path="clients/*"
                element={
                  <PermissionGate section="clients">
                    <ClientsRoutes />
                  </PermissionGate>
                }
              />
              {/* Loyalty (loyalty FE1) — feature gated; hidden entirely when
                  locked (no upsell — rollout is a manual flag for now). */}
              <Route
                path="loyalty/rewards"
                element={
                  <FeatureGate feature="loyalty" hideCompletely>
                    <RewardsPage />
                  </FeatureGate>
                }
              />
              {/* Loyalty reporting shell (§5) — WIP-covered page, also
                  surfaced from the Reports page. */}
              <Route
                path="loyalty/rewards/reporting"
                element={
                  <FeatureGate feature="loyalty" hideCompletely>
                    <LoyaltyReportingPage />
                  </FeatureGate>
                }
              />
              {/* Legacy path (pre-rename) — keep old links working. */}
              <Route
                path="rewards"
                element={<Navigate to="/admin/loyalty/rewards" replace />}
              />
              {/* Notifications became the Alerts popover in the nav bar;
                  old links and bookmarks land on an explanation. */}
              <Route
                path="notifications"
                element={
                  <PageMoved
                    title="Notifications have moved"
                    message="Notifications are now called Alerts and open from the bell in the navigation bar, on any page."
                  />
                }
              />
              <Route
                path="settings/*"
                element={
                  <SettingsPage
                    onBusinessHoursSubmit={handleBusinessHoursSubmit}
                    onServicesSubmit={handleServicesSubmit}
                  />
                }
              />
              <Route
                path="payments/*"
                element={
                  <StripePaymentsProvider>
                    <PaymentsPage />
                  </StripePaymentsProvider>
                }
              />

              {/* Sales tab (FE-2) — behind REACT_APP_SALES_TAB, default OFF.
                  Nests Transactions / Purchases / relocated Cash Drawer. The
                  standalone /admin/cash-drawer route above is kept so flag-OFF
                  behavior is unchanged. */}
              {isSalesTabEnabled() && (
                <Route path="sales/*" element={<SalesPage />} />
              )}

              {/* Website Generator Routes - Only available in test/dev environments */}
              {WEBSITE_FEATURES.WEBSITE_GENERATOR_WIZARD_ENABLED && (
                <Route
                  path="website-generator-wizard"
                  element={
                    <ProtectedRoute allowedRoles={["Owner"]}>
                      <WebsiteGeneratorWizard />
                    </ProtectedRoute>
                  }
                />
              )}

              {/* Marketing Routes - Feature gated; section access from section_permissions (marketing.canView) */}
              <Route
                path="marketing"
                element={
                  <FeatureGate feature="marketing">
                    <MarketingRoutes />
                  </FeatureGate>
                }
              >
                <Route index element={<MarketingPageShell />} />
                <Route path="new" element={<CampaignWizard />} />
                <Route
                  path="groups/:groupId"
                  element={<ClientGroupDetailPage />}
                />
                <Route path=":id" element={<ViewCampaign />} />
              </Route>

              {/* Reports Routes - gated by any report permission
                  (utils/reportAccess hasAnyReportAccess).
                  /admin/reports lists installed reports; the per-report
                  view lives at /admin/reports/:reportId so each report has a
                  shareable URL. ReportsRoutes picks the legacy pages or the
                  Reports & Summaries shell (REACT_APP_REPORTS_V2, default
                  OFF ⇒ the legacy pages render exactly as before). */}
              <Route
                path="reports/*"
                element={
                  <PermissionGate section="reports">
                    <ReportsRoutes />
                  </PermissionGate>
                }
              />

              {/* Chat (two-way SMS/MMS) — behind REACT_APP_MESSAGING_ENABLED,
                  default OFF, and additionally gated at runtime by the messaging
                  permission + Twilio platform (MessagingRoutes). Each
                  conversation has a shareable /admin/chat/:conversationId URL. */}
              {isMessagingEnabled() && (
                <Route path="chat" element={<MessagingRoutes />}>
                  <Route index element={<ChatPage />} />
                  <Route path=":conversationId" element={<ChatPage />} />
                </Route>
              )}

              {/* Payroll Route - Feature gated */}
              <Route
                path="payroll"
                element={
                  <FeatureGate feature="payroll">
                    <PayrollPage />
                  </FeatureGate>
                }
              />

              {/* Voice AI Route - Feature gated */}
              <Route
                path="voice-ai"
                element={
                  <FeatureGate feature="voice_ai">
                    <ProtectedRoute allowedRoles={["Owner", "Manager"]}>
                      <VoiceAIPage />
                    </ProtectedRoute>
                  </FeatureGate>
                }
              />

              <Route
                path="*"
                element={<Navigate to="/admin/calendar" replace />}
              />
            </Routes>
          </AppLayout>
        }
      />
      {/* Default Route — compute landing from access (see POST_SIGNIN_REQUEST_INVENTORY.md) */}
      <Route path="*" element={<LandingRedirect />} />
    </SentryRoutes>
  );
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <AuthProvider>
          <FeatureAccessProvider>
            <SalonProvider>
              <LoyaltyProgramProvider>
                <DiscountPresetsProvider>
                  <OnboardingProvider>
                    <ServiceAdjustmentFormsProvider>
                      <FormTemplatesProvider>
                        <ClientFormsProvider>
                          <ServiceRequiredFormsProvider>
                            <ServicePreAppointmentDocumentsProvider>
                              <ServicePostAppointmentProvider>
                                <CalendarProvider>
                                  <CalendarEventProvider>
                                    <ClientProvider>
                                      <NotificationProvider>
                                        <TransactionProvider>
                                          <ProductsProvider>
                                            <TemplateProvider>
                                              <ResponsiveProvider>
                                                <MantineProvider
                                                  theme={brandTheme}
                                                  cssVariablesResolver={
                                                    brandCssVariablesResolver
                                                  }
                                                >
                                                  <DatesProvider
                                                    settings={{
                                                      firstDayOfWeek: 0,
                                                      weekendDays: [0, 6],
                                                    }}
                                                  >
                                                    <Sentry.ErrorBoundary
                                                      fallback={
                                                        <AppErrorFallback />
                                                      }
                                                      showDialog
                                                    >
                                                      {/* zIndex keeps toasts (e.g. gift card
                                                        notifications) above the Calendar FAB
                                                        (z-index: 999) so they are never covered
                                                        by it. */}
                                                      <Notifications
                                                        zIndex={getDefaultZIndex(
                                                          "max",
                                                        )}
                                                      />
                                                      {/* Unsaved-changes guard: inside the
                                                          router + Mantine so its shared
                                                          dialog can render; wraps every
                                                          route so the nav rail, flyouts,
                                                          avatar menu (sign-out, location),
                                                          mobile nav sheet and Alerts can
                                                          ask before leaving. */}
                                                      <NavigationGuardProvider>
                                                        <NavigationLoadingProvider>
                                                          {/* App-wide so the Chat
                                                            unread badge + realtime
                                                            persist across routes;
                                                            inert unless the inbox
                                                            is enabled for the
                                                            location. */}
                                                          <MessagingProvider>
                                                            <div className="App">
                                                              <AppContent />
                                                            </div>
                                                          </MessagingProvider>
                                                          <NavigationLoadingOverlay />
                                                        </NavigationLoadingProvider>
                                                      </NavigationGuardProvider>
                                                    </Sentry.ErrorBoundary>
                                                  </DatesProvider>
                                                </MantineProvider>
                                              </ResponsiveProvider>
                                            </TemplateProvider>
                                          </ProductsProvider>
                                        </TransactionProvider>
                                      </NotificationProvider>
                                    </ClientProvider>
                                  </CalendarEventProvider>
                                </CalendarProvider>
                              </ServicePostAppointmentProvider>
                            </ServicePreAppointmentDocumentsProvider>
                          </ServiceRequiredFormsProvider>
                        </ClientFormsProvider>
                      </FormTemplatesProvider>
                    </ServiceAdjustmentFormsProvider>
                  </OnboardingProvider>
                </DiscountPresetsProvider>
              </LoyaltyProgramProvider>
            </SalonProvider>
          </FeatureAccessProvider>
        </AuthProvider>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
